/* Ash — infinite with-hints slideshow peek (v2)
 *
 * The previous version added <div> clones inside slideshow-slides, but the
 * theme's CSS keys off the <slideshow-slide> tag for width and aspect
 * ratio — so the divs took on their natural intrinsic size (full video
 * height) and blew the section out to ~200vh.
 *
 * v2 clones REAL <slideshow-slide> elements so they inherit every layout
 * rule (width, snap-align, aspect ratio, etc.) automatically. They then
 * strip the `ref="slides[]"` and `slide-id` attributes so slideshow.js's
 * `refs.slides` array and pagination never see them. The clones are pure
 * visual peek content at the boundaries; navigation indices and the
 * counter "1 / 5" stay correct.
 *
 * Behaviour:
 *   - Prepend a clone of the LAST slide  → shows as left-peek on slide 1.
 *   - Append  a clone of the FIRST slide → shows as right-peek on slide N.
 *   - Initial scroll lands on the first REAL slide (skips past the
 *     leading clone) so peek is visible immediately.
 *   - When the user swipes into a clone, a scroll listener teleports the
 *     scroll position to the corresponding real slide (no animation).
 *   - When slideshow.js's `infinite` wrap-around fires on arrow click,
 *     this module doesn't interfere — slideshow.js scrolls to the real
 *     slide and the clones happen to keep peeking from both sides.
 */

class AshSlideshowInfinitePeek {
  constructor(component) {
    this.component = component;
    this.container = component.querySelector('slideshow-slides');
    if (!this.container) return;

    const allSlides = this.container.querySelectorAll('slideshow-slide');
    this.slides = Array.from(allSlides).filter(
      (s) => !s.hasAttribute('data-ash-clone')
    );
    if (this.slides.length < 2) return;

    this._installClones();
    this._installSnapHandler();
  }

  _installClones() {
    const first = this.slides[0];
    const last = this.slides[this.slides.length - 1];

    this.prevClone = this._makeClone(last, 'prev');
    this.nextClone = this._makeClone(first, 'next');

    this.container.insertBefore(this.prevClone, first);
    this.container.appendChild(this.nextClone);

    /* After clones are in the DOM, scroll the container so the first
     * REAL slide is at the leftmost visible snap position. Without this
     * the user would land on the prev-clone on first paint. */
    requestAnimationFrame(() => this._scrollTo(first, false));
  }

  _makeClone(source, position) {
    const clone = source.cloneNode(true);
    clone.removeAttribute('ref');
    clone.removeAttribute('slide-id');
    clone.removeAttribute('aria-current');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-ash-clone', position);
    clone.inert = true;

    /* Remove ref="" descendants inside (deferred-media, video, etc.) so
     * they don't double-register with framework refs. */
    clone.querySelectorAll('[ref]').forEach((el) => el.removeAttribute('ref'));

    /* Strip block IDs and unique element IDs so duplicate-id warnings
     * don't fire and the framework can't accidentally select the clone. */
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));

    return clone;
  }

  _installSnapHandler() {
    let scrollEndTimer = null;
    this.container.addEventListener(
      'scroll',
      () => {
        clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(() => this._onScrollSettled(), 140);
      },
      { passive: true }
    );
  }

  _onScrollSettled() {
    const containerLeft = this.container.scrollLeft;
    const viewportCenter = containerLeft + this.container.clientWidth / 2;

    /* Which child is the viewport currently centered on? */
    const children = Array.from(this.container.children);
    let centered = null;
    for (const child of children) {
      const childLeft = child.offsetLeft;
      const childRight = childLeft + child.offsetWidth;
      if (childLeft <= viewportCenter && childRight >= viewportCenter) {
        centered = child;
        break;
      }
    }
    if (!centered) return;

    if (centered === this.prevClone) {
      /* Landed on the leading clone (which displays the last slide's
       * content). Teleport to the REAL last slide so the wrap feels
       * seamless. */
      this._scrollTo(this.slides[this.slides.length - 1], false);
    } else if (centered === this.nextClone) {
      this._scrollTo(this.slides[0], false);
    }
  }

  _scrollTo(slide, smooth) {
    const left = slide.offsetLeft + slide.offsetWidth / 2 - this.container.clientWidth / 2;
    this.container.scrollTo({
      left: Math.max(0, left),
      behavior: smooth ? 'smooth' : 'instant',
    });
  }
}

function init() {
  const components = document.querySelectorAll(
    'slideshow-component.slideshow--with-hints[infinite]'
  );
  components.forEach((c) => {
    if (c.dataset.ashLoopInit) return;
    c.dataset.ashLoopInit = '1';
    new AshSlideshowInfinitePeek(c);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => requestAnimationFrame(init));
} else {
  requestAnimationFrame(init);
}

/* Re-init when sections reload in the theme editor */
document.addEventListener('shopify:section:load', () => requestAnimationFrame(init));
